import { type Kmat, type LinearCstic, type LinearSubstep, type SubstepTile } from "./models";
import { type CsticUpdate } from "./api/sendLinearConfiguratorUpdate";
import { CSTIC_VSKITS_NAME } from "./constants";
import { EXTRA_DATA_DIRTY_PARAM_PREFIX, EXTRA_VALIDATION_PARAM_PREFIX } from "~/Configurators/Linear/namingUtils";
import type { KmatType } from "~/Configurators/Linear/kmatType";

/**
 * This function tries to find which tile should be selected based on cstic values coming from the KB
 * @param element - substep
 * @param kmatMap - all cstics grouped per kmat
 */
export function findSelectedTileIndex(element: LinearSubstep, kmatMap: Dict<Kmat[]>) {
  const allTiles = element.tiles.concat(element.groupedTiles.flatMap((group) => group.tiles));
  const potentialTiles = allTiles.filter((tile) => isTileCandidateForSelection(tile, kmatMap, element));
  if (potentialTiles.length === 0) {
    return null;
  }
  if (potentialTiles.length === 1) {
    return allTiles.indexOf(potentialTiles[0]);
  }
  // Potency - how many cstics it sets. If there's a tile that sets 2 cstics and a tile that sets 1, we choose the former
  let tilesWithPotency = potentialTiles.map((tile) => ({
    tile,
    potency: Object.keys(tile.cstics).length,
  }));
  tilesWithPotency.sort((a, b) => b.potency - a.potency);
  const maxPotency = tilesWithPotency[0].potency;
  tilesWithPotency = tilesWithPotency.filter(({ potency }) => potency === maxPotency);
  if (tilesWithPotency.length !== 1) {
    console.warn("Multiple tiles to choose from. Choosing first from following:");
    console.warn(tilesWithPotency);
  }

  const selectedIndex = allTiles.indexOf(tilesWithPotency[0].tile);
  // In case no tile is to be selected, we give back null
  return selectedIndex === -1 ? null : selectedIndex;
}

function isTileCandidateForSelection(tile: SubstepTile, kmatMap: Dict<Kmat[]>, element: LinearSubstep) {
  const mapping = constructCsticToKmatTypeMap(kmatMap);
  return (
    Object.entries(tile.cstics)
      // .filter(([_, value]) => value !== null)
      .every(([csticName, value]) => {
        // Exception for step 7 - substep 1 because VSKITS seems to be on another kmat level, only VKITS comes here
        // TODO Daniel from Marius: please check this and don't kill me
        if (csticName === CSTIC_VSKITS_NAME) {
          return true;
        }
        const kmatType = mapping[csticName];
        const kmats = kmatMap[kmatType];
        if (!kmats?.length) {
          return false;
        }
        return kmats.every((kmat) => {
          const cstic = kmat.cstics[csticName];
          if (!cstic) {
            return false;
          }
          return cstic.value === value;
        });
      })
  );
}

export function constructCsticToKmatTypeMap(kmatDict: Dict<Kmat[]>) {
  const map: Record<string, KmatType> = {};
  Object.values(kmatDict)
    .flatMap((k) => k)
    .forEach((k) => {
      Object.values(k.cstics).forEach((cstic) => (map[cstic.name] = k.type));
    });
  return map;
}

export function csticUpdatesFromDict(
  updates: Dict<string | null | undefined>,
  csticNameToKmatType: Record<string, KmatType>,
): CsticUpdate[] {
  return Object.entries(updates).map(([csticKey, csticValue]) => ({
    key: csticKey,
    type: csticNameToKmatType[csticKey],
    value: csticValue !== undefined ? csticValue : null,
  }));
}

export const getCsticNumberVal = (cstic: LinearCstic | undefined | null) => {
  return !cstic || !cstic.value ? null : +cstic.value;
};
export const getCsticVal = (cstic: LinearCstic | undefined | null) => {
  return !cstic || cstic.value === undefined ? null : cstic.value;
};

export const fromExtraValidationToDirtyParamName = (extraValidationParamName: string) => {
  return extraValidationParamName.replace(EXTRA_VALIDATION_PARAM_PREFIX, EXTRA_DATA_DIRTY_PARAM_PREFIX);
};

export const trimCollapseWhitespace = (text: string) => {
  return text.trim().replace(/\s+/, " ");
};

export function formatCsticsToSet(cstics: { [csticName: string]: string | null }) {
  return Object.entries(cstics)
    .map(([name, value]) => `${name}=${value}`)
    .join(", ");
}
